import React, { useEffect } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import BbanPreview from 'components/BbanPreview';
import { padBbanParts, BBAN_BANK_CODE_REGEX, BBAN_BRANCH_CODE_REGEX, BBAN_ACCOUNT_NUMBER_REGEX } from 'lib/bban';
import { assembleIsraeliIban, IBAN_COUNTRY } from 'lib/iban';

import type { BeneficiaryField, FormValues } from '../types';

interface IbanFieldProps {
  field: BeneficiaryField;
  fields: BeneficiaryField[];
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
}

// Israel-only IBAN preview: assembles the full IBAN — 'IL' + mod-97 check digits
// + the 19-digit BBAN — from the field's `computedFrom` parts ([bankCode,
// branchCode, accountNumber]) and writes it back into the field's own name so it
// is validated/submitted like any visible field. Parts are zero-padded for the
// preview; the assembled value stays '' until every part is filled, so the
// `type: 'iban'` non-empty validation means "all parts filled".
// The check-digits segment shows '00' as a placeholder until then.
const IbanField: React.FC<IbanFieldProps> = ({ field, fields, control, setValue }) => {
  const partNames = field.computedFrom ?? [];
  const partValues = useWatch({ control, name: partNames }) as Array<string | undefined>;

  const rawParts = {
    bankCode: partValues?.[0] ?? '',
    branchCode: partValues?.[1] ?? '',
    accountNumber: partValues?.[2] ?? '',
  };
  // Assemble only once every part is a valid digit string (not merely non-empty)
  // so the preview never shows computed check digits for malformed input.
  const allPartsValid =
    BBAN_BANK_CODE_REGEX.test(rawParts.bankCode) &&
    BBAN_BRANCH_CODE_REGEX.test(rawParts.branchCode) &&
    BBAN_ACCOUNT_NUMBER_REGEX.test(rawParts.accountNumber);
  const iban = allPartsValid ? assembleIsraeliIban(rawParts) : '';
  const padded = padBbanParts(rawParts);
  const paddedByIndex = [padded.bankCode, padded.branchCode, padded.accountNumber];
  const checkDigits = iban ? iban.slice(2, 4) : '00';

  useEffect(() => {
    setValue(field.name, iban, { shouldValidate: true });
  }, [iban, field.name, setValue]);

  // A hidden IBAN field still assembles and writes its value (above) but shows
  // no preview — the user only sees the source parts. The hook order above runs
  // unconditionally, so the early return is safe.
  if (field.hidden) return null;

  const partSegments = partNames.map((partName, i) => ({
    caption: fields.find((f) => f.name === partName)?.label ?? partName,
    value: paddedByIndex[i] ?? partValues?.[i] ?? '',
  }));

  const segments = [
    { caption: 'Country', value: IBAN_COUNTRY },
    { caption: 'Check', value: checkDigits },
    ...partSegments,
  ];

  return <BbanPreview label={field.label} segments={segments} />;
};

export default IbanField;
